import { useEffect, useEffectEvent, useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { areFilterValuesEqual } from '@/utils/filters';

type FilterValues = Record<string, string>;

interface UseFilterDraftOptions<TValues extends FilterValues> {
  appliedValues: TValues;
  onApply: (values: TValues) => void;
  normalizeValues?: ((values: TValues) => TValues) | undefined;
  shouldWaitForApply?: boolean | undefined;
}

export function useFilterDraft<TValues extends FilterValues>({
  appliedValues,
  onApply,
  normalizeValues,
  shouldWaitForApply = false,
}: UseFilterDraftOptions<TValues>) {
  const [draftValues, setDraftValues] = useState(appliedValues);
  const [seenAppliedValues, setSeenAppliedValues] = useState(appliedValues);
  const [lastSentValues, setLastSentValues] = useState<TValues | null>(null);
  const debouncedDraftValues = useDebounce(draftValues);

  if (!areFilterValuesEqual(seenAppliedValues, appliedValues)) {
    setSeenAppliedValues(appliedValues);
    setLastSentValues(null);
    const isOwnApply =
      lastSentValues !== null && areFilterValuesEqual(lastSentValues, appliedValues);
    if (!isOwnApply) setDraftValues(appliedValues);
  }

  const normalize = (values: TValues) => (normalizeValues ? normalizeValues(values) : values);

  function sendValues(values: TValues) {
    const valuesToApply = normalize(values);
    if (areFilterValuesEqual(valuesToApply, appliedValues)) return;
    setLastSentValues(valuesToApply);
    onApply(valuesToApply);
  }

  const sendDebouncedDraft = useEffectEvent((values: TValues) => {
    if (!shouldWaitForApply) sendValues(values);
  });

  useEffect(() => {
    sendDebouncedDraft(debouncedDraftValues);
  }, [debouncedDraftValues]);

  function applyValues(values: TValues) {
    setDraftValues(values);
    sendValues(values);
  }

  return {
    draftValues,
    setDraftValues,
    applyValues,
    hasPendingChanges: !areFilterValuesEqual(normalize(draftValues), appliedValues),
  };
}
